import { shortBoardLabel } from "../../utils/fetchCableTakeoffPaths";
import { buildAgentQuestions } from "./agentQuestions";
import type {
  CableCircuit,
  CablePosition,
  CableProvenance,
  CableViewBundle,
  GapTranslate,
  SldCircuitRow,
  SldCircuits,
  SldIdColumn,
} from "../findCableGaps";
import type {
  UnroutedDevice,
  UnroutedReason,
} from "../unroutedDevices";
import type {
  BuildKarlSteps,
  KarlAssignment,
  KarlCircuit,
  KarlDevice,
  KarlOverview,
  KarlScale,
  KarlSchematicFinding,
  KarlSchematicWay,
  KarlSourceItem,
  KarlSourceOrigin,
  KarlStep,
  KarlUnkeyedGroup,
} from "./types";

/** Fallback when a bundle predates px_per_pt; keeps positions finite, not right. */
const FALLBACK_PX_PER_PT = 1;

const ID_COLUMNS: SldIdColumn[] = ["nr", "breaker", "cable_id"];

/** "-0F5", "0F5" and "0f5" are one tag — the rule the whole join uses. */
const normalizeTag = (value: string): string =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, "");

const toOrigin = (value: string | undefined): KarlSourceOrigin =>
  value === "annotation" || value === "plan_text" || value === "estimated"
    ? value
    : "unknown";

const toPdfPoints = (
  position: CablePosition | undefined,
  pxPerPt: number,
): CablePosition | null =>
  position && typeof position.x === "number" && typeof position.y === "number"
    ? { x: position.x / pxPerPt, y: position.y / pxPerPt }
    : null;

/**
 * `source_id` is the exact key the run routed from, so prefer it; older bundles
 * only carry `board_assigned`, which may abbreviate ("BMZ" vs
 * "BMZ · Brandmeldeanlage"), hence the label comparison as a fallback.
 */
const feedsFrom = (sourceName: string, circuit: CableCircuit): boolean => {
  if (circuit.source_id) return circuit.source_id === sourceName;
  const board = circuit.board_assigned || (circuit.circuit_id ?? "").split("·")[0];
  if (!board) return false;
  return (
    sourceName === board || shortBoardLabel(sourceName) === shortBoardLabel(board)
  );
};

const deviceCountOf = (circuit: CableCircuit): number =>
  circuit.destinations?.length ?? circuit.destinations_count ?? 0;

const lengthOf = (circuit: CableCircuit): number =>
  circuit.estimated_length_m ?? circuit.total_cable_length_m ?? 0;

const toKarlCircuit = (
  circuit: CableCircuit,
  pxPerPt: number,
): KarlCircuit => {
  const circuitId = circuit.circuit_id ?? "";
  const devices: KarlDevice[] = (circuit.destinations ?? []).map(
    (destination, index) => ({
      destinationId: `${circuitId}-${index}`,
      name: destination.element_name || destination.element_id || "",
      position: toPdfPoints(destination.coordinates, pxPerPt),
      reachable: destination.reachable !== false,
    }),
  );
  return {
    circuitId,
    circuitKey: circuit.circuit_assigned?.trim() || null,
    cableType: circuit.cable_spec || circuit.cable_type_estimated || "",
    lengthM: lengthOf(circuit),
    devices,
  };
};

const buildOverview = (
  bundle: CableViewBundle,
  provenance: CableProvenance,
  circuits: CableCircuit[],
  t: GapTranslate,
): KarlOverview => {
  const notes: Array<{ label: string; text: string }> = [];
  const add = (key: string, text: string | undefined): void => {
    if (text?.trim()) notes.push({ label: t(key), text: text.trim() });
  };
  add("smartSurveyor.cablesBeta.stepOverviewWhy", provenance.why);
  add(
    "smartSurveyor.cablesBeta.stepOverviewStructure",
    provenance.project_structure,
  );
  add("smartSurveyor.cablesBeta.stepOverviewTruth", provenance.source_of_truth);
  add("smartSurveyor.cablesBeta.stepOverviewGrouping", provenance.grouping);
  add("smartSurveyor.cablesBeta.stepOverviewRouting", provenance.routing_decision);
  add("smartSurveyor.cablesBeta.stepOverviewSld", provenance.sld_usage);

  return {
    planName: bundle.plan_name ?? "",
    approach: provenance.approach ?? "",
    totalM:
      bundle.cached_batch?.summary?.estimated_total_m ??
      bundle.cached_batch?.summary?.total_cable_length_m ??
      0,
    circuitCount: circuits.length,
    deviceCount: circuits.reduce(
      (total, circuit) => total + deviceCountOf(circuit),
      0,
    ),
    notes,
    warnings: (provenance.warnings ?? []).filter((warning) => warning?.trim()),
    ownerInstructions: provenance.owner_instructions?.trim() ?? "",
  };
};

const buildSources = (
  bundle: CableViewBundle,
  provenance: CableProvenance,
  circuits: CableCircuit[],
  pxPerPt: number,
): KarlSourceItem[] => {
  const positions = bundle.cached_batch?.sources ?? {};
  // A source with no provenance entry is still a source: the positions map is
  // what the geometry actually routed from.
  const names = new Set<string>([
    ...(provenance.sources ?? [])
      .map((source) => source.name ?? "")
      .filter(Boolean),
    ...Object.keys(positions),
  ]);

  return [...names].map((name) => {
    const recorded = (provenance.sources ?? []).find(
      (source) => source.name === name,
    );
    const mine = circuits.filter((circuit) => feedsFrom(name, circuit));
    return {
      name,
      label: shortBoardLabel(name),
      origin: toOrigin(recorded?.origin),
      placedBy: recorded?.placed_by ?? "",
      position: toPdfPoints(positions[name], pxPerPt),
      circuitCount: mine.length,
      deviceCount: mine.reduce(
        (total, circuit) => total + deviceCountOf(circuit),
        0,
      ),
      lengthM: mine.reduce((total, circuit) => total + lengthOf(circuit), 0),
    };
  });
};

const buildScale = (
  bundle: CableViewBundle,
  provenance: CableProvenance,
): KarlScale => {
  const basis = provenance.scale ?? "";
  return {
    pxPerM: typeof bundle.scale === "number" ? bundle.scale : null,
    basis,
    assumed: /DEFAULT/i.test(basis) || typeof bundle.scale !== "number",
  };
};

/**
 * Which schematic column the plan's printed tags came from. The run does not
 * record it, so infer it: the column whose values appear in the circuit keys.
 */
const detectIdColumn = (
  rows: SldCircuitRow[],
  circuits: CableCircuit[],
): SldIdColumn | null => {
  const keys = new Set(
    circuits
      .map((circuit) => circuit.circuit_assigned?.trim())
      .filter((key): key is string => Boolean(key)),
  );
  if (!keys.size) return null;

  let best: SldIdColumn | null = null;
  let bestHits = 0;
  for (const column of ID_COLUMNS) {
    const values = new Set(
      rows
        .map((row) => (row[column] ?? "").toString().trim())
        .filter((value) => value.length > 0),
    );
    let hits = 0;
    keys.forEach((key) => {
      if (values.has(key)) hits += 1;
    });
    if (hits > bestHits) {
      bestHits = hits;
      best = column;
    }
  }
  return best;
};

const buildSchematic = (
  sldCircuits: SldCircuits | null,
  circuits: CableCircuit[],
): KarlSchematicFinding | null => {
  const boards = sldCircuits?.boards;
  if (!boards || !Object.keys(boards).length) return null;
  const rows = Object.values(boards).flat();

  return {
    boards: Object.keys(boards).sort(),
    rowCount: rows.length,
    cableTypes: [
      ...new Set(
        rows
          .map((row) => row.cable_type?.trim())
          .filter((type): type is string => Boolean(type)),
      ),
    ].sort(),
    idColumn: detectIdColumn(rows, circuits),
    keyedCircuits: circuits.filter((circuit) =>
      circuit.circuit_assigned?.trim(),
    ).length,
    totalCircuits: circuits.length,
  };
};

/**
 * Every outgoing way in the schematic, with the devices the takeoff actually
 * put on it. Reserve ways are dropped: a spare breaker is meant to feed
 * nothing, and listing it as unfilled work would bury the ways that matter.
 */
const buildWays = (
  sldCircuits: SldCircuits | null,
  circuits: CableCircuit[],
  idColumn: SldIdColumn | null,
): KarlSchematicWay[] => {
  const boards = sldCircuits?.boards;
  if (!boards) return [];

  // Keyed on the normalised tag, so a circuit finds its row whichever column
  // the schedule happened to print the tag in.
  const byKey = new Map<string, { devices: number; circuitIds: string[] }>();
  for (const circuit of circuits) {
    const key = normalizeTag(circuit.circuit_assigned ?? "");
    if (!key) continue;
    const entry = byKey.get(key) ?? { devices: 0, circuitIds: [] };
    entry.devices += deviceCountOf(circuit);
    if (circuit.circuit_id) entry.circuitIds.push(circuit.circuit_id);
    byKey.set(key, entry);
  }

  const ways: KarlSchematicWay[] = [];
  for (const [board, rows] of Object.entries(boards)) {
    for (const row of rows) {
      const description = row.description?.trim() ?? "";
      const aliases = ID_COLUMNS.map((column) =>
        (row[column] ?? "").toString().trim(),
      ).filter(Boolean);
      // The detected column names the way; the others still identify it. A
      // schedule that puts the plan's tag in `cable_id` on some rows and `nr`
      // on others is not an error we can detect away — it is just how it was
      // typed, and matching on any of them is what finds both.
      const key =
        (idColumn ? (row[idColumn] ?? "").toString().trim() : "") ||
        aliases[0] ||
        "";
      if (!key || description.toLowerCase().includes("reserve")) continue;
      const found = aliases
        .map((alias) => byKey.get(normalizeTag(alias)))
        .find(Boolean);
      ways.push({
        key,
        aliases,
        breaker: (row.breaker ?? "").toString().trim() || key,
        cableId: (row.cable_id ?? "").toString().trim(),
        board,
        description,
        cableType: row.cable_type?.trim() ?? "",
        deviceCount: found?.devices ?? 0,
        circuitIds: found?.circuitIds ?? [],
      });
    }
  }
  // Empty ways first: they are the work, and the filled ones are the evidence
  // that the reading was right at all.
  return ways.sort((a, b) => a.deviceCount - b.deviceCount);
};

const buildAssignments = (
  sources: KarlSourceItem[],
  circuits: CableCircuit[],
  pxPerPt: number,
): KarlAssignment[] => {
  const assignments = sources.map((source) => {
    const mine = circuits
      .filter((circuit) => feedsFrom(source.name, circuit))
      .map((circuit) => toKarlCircuit(circuit, pxPerPt));
    return {
      sourceName: source.name,
      sourceLabel: source.label,
      circuits: mine,
      deviceCount: mine.reduce(
        (total, circuit) => total + circuit.devices.length,
        0,
      ),
      lengthM: mine.reduce((total, circuit) => total + circuit.lengthM, 0),
    };
  });

  // Circuits whose board matches no feed point would otherwise vanish from the
  // walkthrough — the estimator must still see them.
  const claimed = new Set(
    assignments.flatMap((assignment) =>
      assignment.circuits.map((circuit) => circuit.circuitId),
    ),
  );
  const orphans = circuits
    .filter((circuit) => !claimed.has(circuit.circuit_id ?? ""))
    .map((circuit) => toKarlCircuit(circuit, pxPerPt));
  if (orphans.length) {
    assignments.push({
      sourceName: "",
      sourceLabel: "",
      circuits: orphans,
      deviceCount: orphans.reduce(
        (total, circuit) => total + circuit.devices.length,
        0,
      ),
      lengthM: orphans.reduce((total, circuit) => total + circuit.lengthM, 0),
    });
  }

  return assignments.filter((assignment) => assignment.circuits.length > 0);
};

const UNROUTED_REASONS: UnroutedReason[] = [
  "no_circuit_id_reaches_it",
  "every_circuit_vetoed",
  "every_feasible_circuit_full",
];

const toUnroutedReason = (value: string | undefined): UnroutedReason =>
  UNROUTED_REASONS.find((reason) => reason === value) ?? "unknown";

const buildUnrouted = (
  provenance: CableProvenance,
  pxPerPt: number,
): UnroutedDevice[] =>
  (provenance.devices_without_circuit ?? []).map((device) => ({
    id: device.id ?? "",
    name: device.name ?? "",
    system: device.system ?? "",
    reason: toUnroutedReason(device.reason),
    position: toPdfPoints(device.coordinates, pxPerPt),
  }));

const buildUnkeyed = (bundle: CableViewBundle): KarlUnkeyedGroup[] =>
  (bundle.unkeyed_groups ?? [])
    .map((entry) => ({ group: entry.name ?? "", count: entry.count ?? 0 }))
    .filter((entry) => entry.group)
    .sort((a, b) => b.count - a.count);

export const buildKarlSteps: BuildKarlSteps = ({
  bundle,
  sldCircuits,
  t,
}) => {
  const empty = {
    steps: [],
    overview: null,
    sources: [],
    schematicBoards: [],
    agent: buildAgentQuestions(null),
    scale: null,
    schematic: null,
    ways: [],
    tagsOnPlan: [],
    pointsPerM: null,
    markerPositions: {},
    assignments: [],
    unkeyed: [],
    unroutedDevices: [],
  };
  if (!bundle) return empty;

  const provenance = bundle.provenance ?? {};
  const circuits = bundle.cached_batch?.circuits ?? [];
  const pxPerPt = bundle.px_per_pt || FALLBACK_PX_PER_PT;

  const overview = buildOverview(bundle, provenance, circuits, t);
  const sources = buildSources(bundle, provenance, circuits, pxPerPt);
  const scale = buildScale(bundle, provenance);
  const schematic = buildSchematic(sldCircuits, circuits);
  const ways = buildWays(sldCircuits, circuits, schematic?.idColumn ?? null);
  const tagsOnPlan = bundle.circuit_key?.keys_on_plan ?? [];
  const markerPositions: Record<string, CablePosition> = {};
  for (const marker of bundle.board_markers ?? []) {
    const at = toPdfPoints(
      marker.x === undefined || marker.y === undefined
        ? undefined
        : { x: marker.x, y: marker.y },
      pxPerPt,
    );
    if (marker.name && at) markerPositions[marker.name] = at;
  }
  // scale is px per metre and px_per_pt is px per point, so their ratio is
  // points per metre — the space every position in this result is already in.
  const pointsPerM =
    bundle.scale && pxPerPt ? bundle.scale / pxPerPt : null;
  const assignments = buildAssignments(sources, circuits, pxPerPt);
  const unkeyed = buildUnkeyed(bundle);
  const unroutedDevices = buildUnrouted(provenance, pxPerPt);

  const uncertainSources = sources.filter(
    (source) => source.origin !== "annotation",
  ).length;
  const unmatchedCircuits = schematic
    ? schematic.totalCircuits - schematic.keyedCircuits
    : 0;

  // Unfilled ways are the grouping work when the schematic could be read. When
  // it could not, the run's own unkeyed groups are the closest thing to a list.
  const unfilledWays = ways.filter((way) => !way.deviceCount).length;
  const groupingOpen = ways.length
    ? unfilledWays
    : unkeyed.length || (unmatchedCircuits > 0 ? 1 : 0);

  // No "what I did" step. Karl's opening line already says what this plan
  // totals, and a step that asks nothing of the estimator is a page he has to
  // click past before reaching the first thing he can actually change.
  const steps: KarlStep[] = [
    {
      id: "sources",
      title: t("smartSurveyor.cablesBeta.stepSourcesTitle"),
      intent: t("smartSurveyor.cablesBeta.stepSourcesIntent"),
      openCount: uncertainSources,
    },
    {
      id: "grouping",
      title: t("smartSurveyor.cablesBeta.stepGroupingTitle"),
      intent: t("smartSurveyor.cablesBeta.stepGroupingIntent"),
      openCount: groupingOpen,
    },
  ];

  return {
    steps,
    overview,
    sources,
    schematicBoards: schematic?.boards ?? [],
    agent: buildAgentQuestions(bundle),
    scale,
    schematic,
    ways,
    tagsOnPlan,
    pointsPerM,
    markerPositions,
    assignments,
    unkeyed,
    unroutedDevices,
  };
};
